import { DateTime } from 'luxon';
import { check } from '../core.ts';
import { DefaultTimeZone, isMidnight } from './utils.ts';
export { DateTime } from 'luxon';

//
// Standard Time
//
// N.B. The date information of a "Standard Time" is discarded, so you CANNOT do
// date arithmatic using these values. If such is needed, use a true DateTime.
// These times are for humans, so we also zero out milliseconds.

// When representing a time without a day, we always use the same day so that the
// values created on different days are equivalent. DateTime checks validity, so we
// can't just use zeroes. This standard defines midnight as 0 epoch seconds.
//
// TODO: Although 'utc' as values makes sense, is there a non-standard zone that we could use to
// indicate this is a standardTime, or some other trick like using a purposely invalid DateTime?
const standardTimeDate = DateTime.fromMillis(0, { zone: 'utc' });
const standardTimeDateParts = {
  millisecond: 0,
  year: standardTimeDate.year,
  month: standardTimeDate.month,
  day: standardTimeDate.day,
} as const;

// You may pass a DateTime to this and get the expected results.
export function standardTime(params: { hour: number; minute?: number; second?: number }) {
  // Note: We don't spread params here, in case it contains other valid DateTime
  // components.
  return DateTime.fromObject(
    { ...standardTimeDateParts, hour: params.hour, minute: params.minute, second: params.second },
    { zone: standardTimeDate.zone },
  );
}

export function parseStandardTime(time: string) {
  let today = DateTime.fromISO(time, { zone: standardTimeDate.zone });
  if (!today.isValid) {
    today = DateTime.fromFormat(time, 'h:mm:ss a', { zone: standardTimeDate.zone });
  }
  if (!today.isValid) {
    today = DateTime.fromFormat(time, 'h:mm a', { zone: standardTimeDate.zone });
  }
  return today.set(standardTimeDateParts);
}

export function toStandardTimeString(date: DateTime) {
  if (date.second !== 0) {
    return date.toFormat('h:mm:ss a');
  }
  return date.toFormat('h:mm a');
}

export function isStandardTime(date: DateTime) {
  return (
    date.zone.equals(standardTimeDate.zone) &&
    date.year === standardTimeDate.year &&
    date.month === standardTimeDate.month &&
    date.day === standardTimeDate.day
  );
}

//
// Standard Date
//
// N.B. The time information of a "Standard Date" is discarded, so you CANNOT do
// time arithmatic using these values. If such is needed, use a true DateTime.

const standardDateTimeParts = { hour: 0, minute: 0, second: 0, millisecond: 0 };

export function standardDate(params: DateTime<true>): DateTime<true>;
export function standardDate(params: {
  year: number;
  month?: number;
  day?: number;
}): DateTime<true> | DateTime<false>;
export function standardDate(params: { year: number; month?: number; day?: number }) {
  return DateTime.fromObject(
    { year: params.year, month: params.month, day: params.day },
    { zone: standardTimeDate.zone },
  );
  // return date.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
}

export function parseStandardDate(date: string) {
  return DateTime.fromISO(date, { zone: standardTimeDate.zone }).set(standardDateTimeParts);
}

export function isStandardDate(date: DateTime) {
  return date.zone.equals(standardTimeDate.zone) && isMidnight(date);
}

export function standardDateToZone(date: DateTime, zone: string) {
  const result = date.setZone(zone, { keepLocalTime: true });
  check(result.isValid);
  return result;
}

export function standardDateToLocal(date: DateTime) {
  // DNS!!: Handle the fact that local time zone varies from server (UTC) to client.
  const localTimeZone = DefaultTimeZone;
  // const localTimeZone = 'local';
  return standardDateToZone(date, localTimeZone);
}

// TODO: require zone.
export function dateToday(zone?: string): DateTime<true> {
  zone ??= DefaultTimeZone;
  const result = DateTime.now()
    .setZone(zone)
    .set(standardDateTimeParts)
    .setZone(standardTimeDate.zone, { keepLocalTime: true });
  check(result.isValid);
  return result;
}

export function dateYesterday(zone?: string): DateTime<true> {
  return dateToday(zone).minus({ days: 1 });
}
